import { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { notification } from 'antd';
import { useMutation } from '@apollo/client';
import PropTypes from 'prop-types';

import { DISCONNECT_PIPEDRIVE } from 'graphql/mutations/integrationMutation';
import { userVar } from 'graphql/cache';
import IntegrationItem from '../IntegrationItem';
import images from 'constants/images';
import utils from 'utils/utils';

const PipedriveIntegration = ({ data, isModalItem, userRole, refetchUser }) => {
  const [integrationData, setIntegrationData] = useState(data);
  const [loading, setLoading] = useState();
  const user = userVar();
  const pipedriveConnectionStatus = utils.getQueryStringValue('pipedrive');

  useEffect(() => {
    if (data) {
      setIntegrationData(data);
    }
  }, [data]);

  useEffect(() => {
    async function checkUser() {
      if (
        pipedriveConnectionStatus === 'connected' &&
        (!user?.pipedrive || Object.keys(user?.pipedrive).length === 0)
      ) {
        const userData = await refetchUser();
        if (userData) {
          userVar({ ...user, ...userData.data.fetchUser });
          setIntegrationData(userData.data.fetchUser.pipedrive);
          setLoading(Object.keys(userData?.data?.fetchUser?.pipedrive).length ? 'connected' : '');
        } else {
          setIntegrationData(user.pipedrive);
          setLoading(Object.keys(user.pipedrive).length ? 'connected' : '');
        }
      } else if (pipedriveConnectionStatus === 'failed') {
        setLoading('');
      }
    }
    checkUser();
  }, [pipedriveConnectionStatus, refetchUser, user]);

  const handleConnect = async () => {
    const pipedriveState = uuidv4();
    sessionStorage.setItem('pipedrive-state', pipedriveState);
    const url = `https://oauth.pipedrive.com/oauth/authorize?client_id=${process.env.REACT_APP_PIPEDRIVE_CLIENT_ID}&redirect_uri=${process.env.REACT_APP_API_URL}/api/pipedrive/oauth&state=${pipedriveState}`;
    await utils.refetchUserOnClosingTab(url);
    const userData = await refetchUser();
    if (userData) {
      userVar({ ...user, ...userData.data.fetchUser });
      setIntegrationData(userData.data.fetchUser.pipedrive);
      setLoading(Object.keys(userData?.data?.fetchUser?.pipedrive || {}).length ? 'connected' : '');
    } else {
      setIntegrationData(user.pipedrive);
      setLoading(Object.keys(user.pipedrive).length ? 'connected' : '');
    }
  };

  const handleClick = () => {
    if (!integrationData || !Object.keys(integrationData).length) {
      setLoading('connecting');
      return handleConnect();
    }
    setLoading('disconnecting');
    return disconnectPipedrive();
  };

  const [disconnectPipedrive] = useMutation(DISCONNECT_PIPEDRIVE, {
    onCompleted: async () => {
      const userData = await refetchUser();
      if (userData) {
        userVar({ ...user, ...userData.data.fetchUser });
      }
      setIntegrationData('');
      setLoading('');
      notification.success({
        message: 'Pipedrive Disconnected',
        description: 'Your account has been disconnected from Pipedrive.',
      });
    },
    onError: () => {
      notification.error({
        message: 'Pipedrive Disconnected Failed',
        description: 'Your account failed to disconnect try again.',
      });
    },
  });

  return (
    <IntegrationItem
      type="pipedrive"
      name="pipedrive"
      description="Create proposals for contacts and deals from Pipedrive"
      integrationLink="https://support.goprospero.com/article/show/176343-how-to-integrate-prospero-with-pipedrive"
      status={
        loading ||
        (integrationData?.token && Object.keys(integrationData.token).length
          ? 'connected'
          : 'disconnected')
      }
      image={images.PIPEDRIVE_ICON}
      handleClick={handleClick}
      isModalItem={isModalItem}
      userRole={userRole}
      user={user}
    />
  );
};

PipedriveIntegration.defaultProps = {
  data: null,
  isModalItem: false,
  userRole: '',
  refetchUser: () => {},
};

PipedriveIntegration.propTypes = {
  data: PropTypes.oneOfType([
    PropTypes.instanceOf(Object),
    PropTypes.instanceOf(Array),
    PropTypes.instanceOf(null),
  ]),
  isModalItem: PropTypes.bool,
  userRole: PropTypes.string,
  refetchUser: PropTypes.func,
};

export default PipedriveIntegration;
